import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { OnboardingQuestionConfig } from '@Models';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { mapAnswersToRequest, mapPreferencesToAnswers } from '../utils/onboardingSteps';
import PreferenceField from './PreferenceField';

interface PreferencesModalProps {
  questions: OnboardingQuestionConfig[];
  sourceName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const PreferencesModal: React.FC<PreferencesModalProps> = ({ questions, sourceName, isOpen, onClose }) => {
  const { t } = useTranslation();
  const { preferences, updatePreferences, deletePreferences, isUpdating, isDeleting, clearSkip } =
    useUserPreferences(sourceName);

  const [currentAnswers, setCurrentAnswers] = useState<Record<string, string | string[]>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (preferences) {
      setCurrentAnswers(mapPreferencesToAnswers(preferences, questions));
    }
  }, [preferences, questions]);

  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleFieldChange = useCallback((key: string, value: string | string[]) => {
    setCurrentAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = async () => {
    const request = mapAnswersToRequest(currentAnswers, questions);
    await updatePreferences(request);
    onClose();
  };

  const handleDelete = async () => {
    await deletePreferences();
    clearSkip();
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[102] flex items-center justify-center bg-[#0f0f1a]/80 backdrop-blur-[8px]"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (e.target === e.currentTarget) onClose();
          }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="w-full max-w-lg max-h-[85vh] bg-[#1a1a2e] rounded-2xl border border-white/10 flex flex-col overflow-hidden mx-4 box-border">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
              <div>
                <h2 className="text-white text-lg font-semibold">{t('Agent.PreferencesModalTitle')}</h2>
                <p className="text-white/40 text-sm mt-0.5">{t('Agent.PreferencesModalSubtitle')}</p>
              </div>
              <TrackedButton
                type="button"
                variant="ghost"
                buttonName="AGENT_PREFERENCES_CLOSE"
                onClick={onClose}
                className="h-auto p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors cursor-pointer flex items-center justify-center">
                <X className="w-4 h-4" />
              </TrackedButton>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-2 min-w-0">
              {questions.map((step) => (
                <PreferenceField
                  key={step.key}
                  step={step}
                  value={currentAnswers[step.key]}
                  onChange={handleFieldChange}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center px-6 py-3 h-[56px] border-t border-white/10 flex-shrink-0">
              <AnimatePresence mode="wait">
                {showDeleteConfirm ? (
                  <motion.div
                    key="confirm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-center gap-3 w-full">
                    <span className="text-white/50 text-xs">{t('Agent.PreferencesClearConfirm')}</span>
                    <TrackedButton
                      type="button"
                      variant="ghost"
                      buttonName="AGENT_PREFERENCES_DELETE_CANCEL"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="h-auto px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white bg-white/5 hover:bg-white/10 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                      {t('Agent.PreferencesClearCancel')}
                    </TrackedButton>
                    <TrackedButton
                      type="button"
                      variant="ghost"
                      buttonName="AGENT_PREFERENCES_DELETE_CONFIRM"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="h-auto px-3 py-1.5 rounded-lg text-xs text-red-400 hover:text-white bg-red-500/10 hover:bg-red-500/30 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap gap-1">
                      {isDeleting ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                          {t('Agent.PreferencesDeleting')}
                        </>
                      ) : (
                        t('Agent.PreferencesClearConfirmAction')
                      )}
                    </TrackedButton>
                  </motion.div>
                ) : (
                  <motion.div
                    key="default"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex items-center justify-between w-full">
                    <TrackedButton
                      type="button"
                      variant="ghost"
                      buttonName="AGENT_PREFERENCES_DELETE_OPEN"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="h-auto p-0 flex items-center gap-1.5 text-white/30 hover:text-red-400 hover:bg-transparent text-xs transition-colors cursor-pointer bg-transparent">
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('Agent.PreferencesClearAll')}
                    </TrackedButton>
                    <TrackedButton
                      type="button"
                      variant="ghost"
                      buttonName="AGENT_PREFERENCES_SAVE"
                      onClick={handleSave}
                      disabled={isUpdating}
                      className="h-auto min-w-[140px] px-5 py-1.5 rounded-lg bg-[#7c6cf6] hover:bg-[#6b5ce5] text-white text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
                      {isUpdating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                          {t('Agent.PreferencesSaving')}
                        </>
                      ) : (
                        t('Agent.PreferencesSave')
                      )}
                    </TrackedButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default PreferencesModal;
